import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/common/Combobox";
import { useResource } from "@/hooks/useResource";
import api, { apiErrorMessage } from "@/lib/api";
import { fmtCurrency, fmtNumber } from "@/lib/format";
import { productTypeLabel, sectionBucket, PRODUCT_TYPES } from "@/lib/production";
import { toast } from "sonner";
import {
  Plus, Trash2, Loader2, ShieldAlert, ChevronDown, ChevronRight, Package, Info, Link2, PackagePlus,
} from "lucide-react";

const COMMON_UNITS = ["kg", "g", "pcs", "ml", "l", "pkt", "dozen", "box", "tray"];
const emptyLine = () => ({ kind: "ingredient", ingredientId: "", refId: "", name: "", unit: "", qty: "" });
const emptyPack = () => ({ name: "", qty: "", unit: "", rate: "" });

export function RecipeEditor({ open, onOpenChange, mode, initialValues, onSubmit, submitting }) {
  const { data: prodData, refetch: refetchProducts } = useResource("/products", { enabled: open });
  const { data: ingData } = useResource("/ingredients", { enabled: open });
  const products = useMemo(() => (Array.isArray(prodData) ? prodData : []), [prodData]);
  const ingredients = useMemo(() => (Array.isArray(ingData) ? ingData : []), [ingData]);

  const [section, setSection] = useState("BAKERY");
  const [showInactive, setShowInactive] = useState(false);
  const [productId, setProductId] = useState(""); // internal product.id (UUID)
  const [initialProductCode, setInitialProductCode] = useState(""); // productId (code) at edit open
  const [relinking, setRelinking] = useState(false); // edit-mode: user chose to re-point product
  const [form, setForm] = useState({});
  const [lines, setLines] = useState([emptyLine()]);
  const [packaging, setPackaging] = useState([]);
  const [packOpen, setPackOpen] = useState(false);
  const [step, setStep] = useState("edit");
  const [confirm, setConfirm] = useState(null); // 'draft' | 'submit'
  const [newProdOpen, setNewProdOpen] = useState(false);
  const [newProd, setNewProd] = useState({ name: "", productType: "BAKERY" });
  const [creatingProduct, setCreatingProduct] = useState(false);

  useEffect(() => {
    if (!open) return;
    const iv = initialValues || {};
    setStep("edit"); setConfirm(null); setRelinking(false); setPackOpen(Array.isArray(iv.packaging) && iv.packaging.length > 0);
    setForm({
      category: iv.category || "Bakery",
      yieldQty: iv.yieldQty ?? 1, yieldUnit: iv.yieldUnit || "pcs",
      theoreticalYield: iv.theoreticalYield ?? 0,
      labourCost: iv.labourCost ?? 0, overheadCost: iv.overheadCost ?? 0, otherCost: iv.otherCost ?? 0,
      packagingCost: iv.packagingCost ?? 0, targetSellingPrice: iv.targetSellingPrice ?? 0,
      productName: iv.productName || "",
    });
    setLines(mergeComponents(iv)
      ? mergeComponents(iv)
      : [emptyLine()]);
    setPackaging(Array.isArray(iv.packaging) ? iv.packaging.map((p) => ({ name: p.name || p.material || "", qty: p.qty ?? p.quantity ?? "", unit: p.unit || "", rate: p.rate ?? p.cost ?? "" })) : []);
    // resolve product on edit
    if (mode === "edit" && iv.productId) {
      const p = (Array.isArray(prodData) ? prodData : []).find((x) => x.productId === iv.productId || x.id === iv.productId);
      setProductId(p?.id || "");
      setInitialProductCode(iv.productId);
      if (p) setSection(sectionBucket(p.productType) === "bakery" ? "BAKERY" : "NON_BAKERY");
    } else { setProductId(""); setInitialProductCode(""); }
  }, [open, initialValues, mode, prodData]);

  const selectedProduct = products.find((p) => p.id === productId);
  const productLocked = mode === "edit" && !relinking;
  const productChanged = mode === "edit" && !!selectedProduct && selectedProduct.productId !== initialProductCode;
  const pickList = products
    .filter((p) => showInactive || p.active)
    .filter((p) => (sectionBucket(p.productType) === "bakery" ? "BAKERY" : "NON_BAKERY") === section);

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, emptyLine()]);
  const removeLine = (i) => setLines((ls) => (ls.length === 1 ? [emptyLine()] : ls.filter((_, idx) => idx !== i)));

  // Combined component options: raw ingredients + made products (semi/finished) as sub-components.
  const componentOptions = useMemo(() => [
    ...ingredients.map((ing) => ({ value: `ing:${ing.id}`, label: ing.name,
      hint: `Ingredient · ${ing.consumptionUnit || ing.unit || ""}${ing.rate ? ` · ₹${ing.rate}` : ""}` })),
    ...products.filter((p) => p.id !== productId).map((p) => ({ value: `prod:${p.id}`, label: p.name,
      hint: `Product · ${p.productId}${p.currentUnitCost ? ` · ₹${fmtNumber(p.currentUnitCost)}/${p.productUnit || "unit"}` : ""}` })),
  ], [ingredients, products, productId]);

  const pickComponent = (i, value) => {
    if (!value) return;
    if (lines.some((l, idx) => idx !== i && (l.kind === "product" ? `prod:${l.refId}` : `ing:${l.ingredientId}`) === value)) {
      toast.error("That item is already in this recipe.");
      return;
    }
    const [kind, id] = value.split(":");
    if (kind === "prod") {
      const p = products.find((x) => x.id === id);
      setLine(i, { kind: "product", refId: id, ingredientId: "", name: p?.name || "", unit: p?.productUnit || p?.yieldUnit || "pcs" });
    } else {
      const ing = ingredients.find((x) => x.id === id);
      setLine(i, { kind: "ingredient", ingredientId: id, refId: "", name: ing?.name || "", unit: ing?.consumptionUnit || ing?.unit || "" });
    }
  };

  const lineValue = (l) => (l.kind === "product" ? (l.refId ? `prod:${l.refId}` : "") : (l.ingredientId ? `ing:${l.ingredientId}` : ""));
  const lineRate = (l) => {
    if (l.kind === "product") return Number(products.find((x) => x.id === l.refId)?.currentUnitCost) || 0;
    return Number(ingredients.find((x) => x.id === l.ingredientId)?.rate) || 0;
  };

  const createInlineProduct = async () => {
    if (!newProd.name.trim()) return toast.error("Product name is required");
    setCreatingProduct(true);
    try {
      const res = await api.post("/products", { name: newProd.name.trim(), productType: newProd.productType, active: true });
      const created = res.data || {};
      await refetchProducts();
      setSection(newProd.productType === "BAKERY" ? "BAKERY" : "NON_BAKERY");
      setProductId(created.id);
      toast.success(`Product created (${created.productId || ""})`);
      setNewProdOpen(false); setNewProd({ name: "", productType: "BAKERY" });
    } catch (e) { toast.error(apiErrorMessage(e, "Could not create product")); }
    finally { setCreatingProduct(false); }
  };

  const setPack = (i, patch) => setPackaging((ps) => ps.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));

  // Estimated costs (final Current/Decided cost is computed by the ERP costing engine on save)
  const estIngredientCost = lines.reduce((s, l) => s + (Number(l.qty) || 0) * lineRate(l), 0);
  const estPackagingCost = packaging.reduce((s, p) => s + (Number(p.qty) || 0) * (Number(p.rate) || 0), 0) || Number(form.packagingCost) || 0;
  const otherTotal = (Number(form.labourCost) || 0) + (Number(form.overheadCost) || 0) + (Number(form.otherCost) || 0);
  const estCurrentCost = estIngredientCost + estPackagingCost + otherTotal;
  const yieldQ = Number(form.yieldQty) || 0;
  const estUnitCost = yieldQ > 0 ? estCurrentCost / yieldQ : 0;

  const validLines = lines.filter((l) => (l.kind === "product" ? l.refId : l.ingredientId) && l.qty !== "" && Number(l.qty) > 0);
  const canProceed = !!selectedProduct && validLines.length > 0 && yieldQ > 0;

  const buildPayload = () => ({
    productName: selectedProduct?.name || form.productName,
    productId: selectedProduct?.productId, sku: selectedProduct?.sku, productKey: selectedProduct?.productKey,
    category: form.category,
    yieldQty: yieldQ, yieldUnit: form.yieldUnit, theoreticalYield: Number(form.theoreticalYield) || 0,
    packagingCost: estPackagingCost,
    labourCost: Number(form.labourCost) || 0, overheadCost: Number(form.overheadCost) || 0, otherCost: Number(form.otherCost) || 0,
    targetSellingPrice: Number(form.targetSellingPrice) || 0,
    ingredients: validLines.filter((l) => l.kind !== "product").map((l) => ({ ingredientId: l.ingredientId, name: l.name, unit: l.unit, qty: Number(l.qty) || 0 })),
    subComponents: validLines.filter((l) => l.kind === "product").map((l) => ({ productRef: l.refId, name: l.name, unit: l.unit, qty: Number(l.qty) || 0, unitCost: lineRate(l) })),
    packaging: packaging.filter((p) => p.name && p.qty !== "").map((p) => ({ name: p.name, qty: Number(p.qty) || 0, unit: p.unit, rate: Number(p.rate) || 0 })),
  });

  const doConfirm = async () => {
    const action = confirm; setConfirm(null);
    await onSubmit(buildPayload(), action);
  };

  const Costing = () => (
    <div className="rounded-lg border border-stone-200 bg-stone-50/60 p-3 text-sm">
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] text-stone-500"><Info className="h-3.5 w-3.5" /> Estimated from current ingredient prices. Final <strong>Current</strong> &amp; <strong>Decided</strong> cost are computed by the ERP costing engine on save.</div>
      <CostRow label="Ingredient cost" value={fmtCurrency(estIngredientCost)} />
      <CostRow label="Packaging cost" value={fmtCurrency(estPackagingCost)} />
      <CostRow label="Other (labour + overhead + other)" value={fmtCurrency(otherTotal)} />
      <div className="my-1 border-t border-stone-200" />
      <CostRow label="Estimated current cost (batch)" value={fmtCurrency(estCurrentCost)} strong />
      <CostRow label="Estimated unit cost" value={`${fmtCurrency(estUnitCost)} / ${form.yieldUnit || "unit"}`} />
      {mode === "edit" && initialValues?.decidedCost != null && (
        <CostRow label="Decided cost (ERP)" value={fmtCurrency(initialValues.decidedCost)} />
      )}
    </div>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[92vh] overflow-y-auto bg-white sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">{step === "review" ? "Review recipe" : mode === "edit" ? "Edit recipe" : "Create recipe"}</DialogTitle>
            <DialogDescription className="text-xs">
              {step === "review" ? "Check the details below, then submit for approval." : "Saved to the shared ERP. New recipes start as Draft; a supplier price change never bumps the version."}
            </DialogDescription>
          </DialogHeader>

          {step === "edit" ? (
            <div className="space-y-4">
              {/* PRODUCT */}
              <div className="rounded-lg border border-stone-200 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <Label className="text-sm font-semibold text-stone-800">Product</Label>
                  <div className="flex items-center gap-1.5">
                    {mode === "edit" && !relinking && (
                      <Button type="button" size="sm" variant="outline" data-testid="recipe-relink-product"
                        className="h-8 gap-1.5 text-amber-700" onClick={() => { setRelinking(true); }}>
                        <Link2 className="h-3.5 w-3.5" /> Re-link product
                      </Button>
                    )}
                    {PRODUCT_TYPES.map((t) => (
                      <Button key={t.value} type="button" size="sm" variant={section === t.value ? "default" : "outline"}
                        data-testid={`recipe-section-${t.value}`} disabled={productLocked}
                        className={section === t.value ? "h-8 bg-amber-700 hover:bg-amber-800" : "h-8"}
                        onClick={() => { setSection(t.value); setProductId(""); }}>{t.label}</Button>
                    ))}
                  </div>
                </div>
                <Combobox testId="recipe-product" value={productId} onChange={setProductId} disabled={productLocked}
                  placeholder="Search product…"
                  options={pickList.map((p) => ({ value: p.id, label: p.name, hint: p.productId }))} />
                {(mode === "create" || relinking) && !newProdOpen && (
                  <button type="button" data-testid="recipe-new-product-toggle" onClick={() => { setNewProd({ name: "", productType: section }); setNewProdOpen(true); }}
                    className="mt-2 flex items-center gap-1 text-[11px] font-medium text-amber-700 hover:underline">
                    <PackagePlus className="h-3.5 w-3.5" /> New product
                  </button>
                )}
                {(mode === "create" || relinking) && newProdOpen && (
                  <div className="mt-2 space-y-2 rounded-lg border border-amber-200 bg-amber-50/50 p-3" data-testid="recipe-new-product-form">
                    <div className="text-xs font-semibold text-stone-700">Create a new product</div>
                    <Input data-testid="recipe-new-product-name" placeholder="Product name" value={newProd.name} onChange={(e) => setNewProd((s) => ({ ...s, name: e.target.value }))} className="h-9 text-sm" />
                    <div className="flex items-center gap-1.5">
                      {PRODUCT_TYPES.map((t) => (
                        <Button key={t.value} type="button" size="sm" variant={newProd.productType === t.value ? "default" : "outline"}
                          data-testid={`recipe-new-product-type-${t.value}`}
                          className={newProd.productType === t.value ? "h-8 bg-amber-700 hover:bg-amber-800" : "h-8"}
                          onClick={() => setNewProd((s) => ({ ...s, productType: t.value }))}>{t.label}</Button>
                      ))}
                      <div className="flex-1" />
                      <Button type="button" size="sm" variant="ghost" className="h-8" data-testid="recipe-new-product-cancel" onClick={() => setNewProdOpen(false)}>Cancel</Button>
                      <Button type="button" size="sm" className="h-8 gap-1.5 bg-amber-700 hover:bg-amber-800" data-testid="recipe-new-product-save" disabled={creatingProduct || !newProd.name.trim()} onClick={createInlineProduct}>
                        {creatingProduct ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Create &amp; select
                      </Button>
                    </div>
                  </div>
                )}
                {selectedProduct && (
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-stone-500">
                    <span>Product ID: <strong className="text-stone-700">{selectedProduct.productId}</strong></span>
                    <span>Type: <strong className="text-stone-700">{productTypeLabel(selectedProduct.productType)}</strong></span>
                    {selectedProduct.sku && <span>SKU: <strong className="text-stone-700">{selectedProduct.sku}</strong></span>}
                  </div>
                )}
                {mode === "edit" && relinking && (
                  <div className="mt-2 rounded bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">
                    Re-linking mode: choose the product this recipe produces. On save the recipe is re-pointed to
                    the selected product{initialProductCode ? <> (currently <strong>{initialProductCode}</strong>)</> : null}.
                    {" "}
                    <button type="button" data-testid="recipe-relink-cancel" className="underline"
                      onClick={() => { setRelinking(false); const p = products.find((x) => x.productId === initialProductCode); setProductId(p?.id || ""); }}>
                      Keep current product
                    </button>
                  </div>
                )}
                {(mode === "create" || relinking) && (
                  <label className="mt-2 flex items-center gap-1.5 text-[11px] text-stone-400">
                    <input type="checkbox" data-testid="recipe-show-inactive" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> Show inactive products
                  </label>
                )}
              </div>

              {/* INGREDIENTS */}
              <div className="rounded-lg border border-stone-200">
                <div className="flex items-center justify-between border-b border-stone-200 bg-stone-50 px-4 py-2">
                  <span className="text-sm font-semibold text-stone-800">Ingredients &amp; Components</span>
                  <Button data-testid="recipe-add-line" size="sm" variant="outline" className="h-8 gap-1.5" onClick={addLine}><Plus className="h-3.5 w-3.5" /> Add line</Button>
                </div>
                <div className="divide-y divide-stone-100">
                  <div className="grid grid-cols-12 gap-2 px-3 py-1.5 text-[10px] font-semibold uppercase text-stone-400">
                    <span className="col-span-6">Ingredient / Product</span><span className="col-span-3">Qty</span><span className="col-span-2">Unit</span>
                  </div>
                  {lines.map((l, i) => (
                    <div key={i} className="grid grid-cols-12 items-center gap-2 px-3 py-2">
                      <div className="col-span-6">
                        <Combobox testId={`recipe-line-ing-${i}`} value={lineValue(l)} onChange={(v) => pickComponent(i, v)} placeholder="Search ingredient or product…"
                          options={componentOptions} />
                        {l.kind === "product" && l.refId && <span className="mt-0.5 inline-block rounded bg-violet-100 px-1.5 text-[9px] font-semibold uppercase text-violet-700">Sub-component (product)</span>}
                      </div>
                      <Input data-testid={`recipe-line-qty-${i}`} type="number" min="0" placeholder="Qty" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} className="col-span-3 h-9 text-sm" />
                      <div className="col-span-2">
                        <Input data-testid={`recipe-line-unit-${i}`} list="recipe-unit-list" placeholder="unit" value={l.unit} onChange={(e) => setLine(i, { unit: e.target.value })} className="h-9 text-sm" />
                      </div>
                      <button data-testid={`recipe-line-remove-${i}`} onClick={() => removeLine(i)} className="col-span-1 text-stone-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                  <datalist id="recipe-unit-list">{COMMON_UNITS.map((u) => <option key={u} value={u} />)}</datalist>
                </div>
              </div>

              {/* YIELD */}
              <div className="rounded-lg border border-stone-200 p-3">
                <div className="mb-2 text-sm font-semibold text-stone-800">Yield</div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Yield (per batch)</Label><Input data-testid="recipe-yieldQty" type="number" min="0" value={form.yieldQty} onChange={(e) => setField("yieldQty", e.target.value)} className="h-9 text-sm" /></div>
                  <div className="space-y-1"><Label className="text-xs">Yield unit</Label><Input data-testid="recipe-yieldUnit" value={form.yieldUnit} onChange={(e) => setField("yieldUnit", e.target.value)} className="h-9 text-sm" /></div>
                  <div className="space-y-1"><Label className="text-xs">Theoretical yield</Label><Input data-testid="recipe-theoreticalYield" type="number" min="0" value={form.theoreticalYield} onChange={(e) => setField("theoreticalYield", e.target.value)} className="h-9 text-sm" /></div>
                </div>
                {yieldQ > 0 && <div className="mt-2 rounded bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">This recipe produces <strong>{fmtNumber(yieldQ)} {form.yieldUnit}</strong> per batch. Production planning scales ingredients from this yield.</div>}
              </div>

              {/* PACKAGING (collapsible) */}
              <div className="rounded-lg border border-stone-200">
                <button data-testid="recipe-packaging-toggle" onClick={() => setPackOpen((o) => !o)} className="flex w-full items-center justify-between px-4 py-2.5 text-sm font-semibold text-stone-800">
                  <span className="flex items-center gap-2"><Package className="h-4 w-4 text-stone-500" /> Packaging <span className="text-xs font-normal text-stone-400">(optional)</span></span>
                  {packOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
                {packOpen && (
                  <div className="border-t border-stone-100 p-3">
                    <div className="space-y-2">
                      {packaging.map((p, i) => (
                        <div key={i} className="grid grid-cols-12 items-center gap-2">
                          <Input data-testid={`recipe-pack-name-${i}`} placeholder="Packaging item" value={p.name} onChange={(e) => setPack(i, { name: e.target.value })} className="col-span-5 h-9 text-sm" />
                          <Input data-testid={`recipe-pack-qty-${i}`} type="number" placeholder="Qty" value={p.qty} onChange={(e) => setPack(i, { qty: e.target.value })} className="col-span-2 h-9 text-sm" />
                          <Input data-testid={`recipe-pack-unit-${i}`} placeholder="Unit" value={p.unit} onChange={(e) => setPack(i, { unit: e.target.value })} className="col-span-2 h-9 text-sm" />
                          <Input data-testid={`recipe-pack-rate-${i}`} type="number" placeholder="Rate" value={p.rate} onChange={(e) => setPack(i, { rate: e.target.value })} className="col-span-2 h-9 text-sm" />
                          <button data-testid={`recipe-pack-remove-${i}`} onClick={() => setPackaging((ps) => ps.filter((_, idx) => idx !== i))} className="col-span-1 text-stone-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      ))}
                    </div>
                    <Button data-testid="recipe-pack-add" size="sm" variant="outline" className="mt-2 h-8 gap-1.5" onClick={() => setPackaging((ps) => [...ps, emptyPack()])}><Plus className="h-3.5 w-3.5" /> Add packaging</Button>
                  </div>
                )}
              </div>

              {/* OTHER COSTS + TARGET */}
              <div className="grid grid-cols-4 gap-3">
                {[["labourCost", "Labour"], ["overheadCost", "Overhead"], ["otherCost", "Other"], ["targetSellingPrice", "Target price"]].map(([k, lab]) => (
                  <div key={k} className="space-y-1"><Label className="text-xs">{lab}</Label><Input data-testid={`recipe-${k}`} type="number" value={form[k]} onChange={(e) => setField(k, e.target.value)} className="h-9 text-sm" /></div>
                ))}
              </div>

              <Costing />
            </div>
          ) : (
            /* REVIEW STEP */
            <div className="space-y-4 text-sm">
              <div className="rounded-lg border border-stone-200 p-4">
                <div className="font-display text-base font-semibold text-stone-900">{selectedProduct?.name}</div>
                <div className="mt-0.5 flex flex-wrap gap-x-5 text-xs text-stone-500">
                  <span>Product ID: {selectedProduct?.productId}</span>
                  <span>Type: {productTypeLabel(selectedProduct?.productType)}</span>
                  <span>Version: {mode === "edit" ? initialValues?.version : 1}</span>
                  <span>Yield: {fmtNumber(yieldQ)} {form.yieldUnit}</span>
                </div>
                {productChanged && (
                  <div className="mt-2 inline-flex items-center gap-1.5 rounded bg-amber-50 px-2.5 py-1 text-xs text-amber-800">
                    <Link2 className="h-3.5 w-3.5" /> Re-linking from <strong>{initialProductCode}</strong> to <strong>{selectedProduct?.productId}</strong>
                  </div>
                )}
              </div>
              <div className="rounded-lg border border-stone-200">
                <div className="border-b border-stone-200 bg-stone-50 px-4 py-2 text-sm font-semibold">Ingredients</div>
                <div className="divide-y divide-stone-100">
                  {validLines.map((l, i) => (
                    <div key={i} className="flex justify-between px-4 py-1.5 text-sm"><span className="text-stone-700">{l.name}</span><span className="font-mono">{fmtNumber(l.qty)} {l.unit}</span></div>
                  ))}
                </div>
              </div>
              {packaging.filter((p) => p.name).length > 0 && (
                <div className="rounded-lg border border-stone-200">
                  <div className="border-b border-stone-200 bg-stone-50 px-4 py-2 text-sm font-semibold">Packaging</div>
                  <div className="divide-y divide-stone-100">
                    {packaging.filter((p) => p.name).map((p, i) => (
                      <div key={i} className="flex justify-between px-4 py-1.5 text-sm"><span className="text-stone-700">{p.name}</span><span className="font-mono">{fmtNumber(p.qty)} {p.unit}</span></div>
                    ))}
                  </div>
                </div>
              )}
              <Costing />
            </div>
          )}

          <DialogFooter className="mt-1 gap-2">
            {step === "edit" ? (
              <>
                <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="recipe-cancel">Cancel</Button>
                <Button variant="outline" data-testid="recipe-save-draft" disabled={!canProceed || submitting} onClick={() => setConfirm("draft")}>
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Draft"}
                </Button>
                <Button className="bg-amber-700 hover:bg-amber-800" data-testid="recipe-to-review" disabled={!canProceed} onClick={() => setStep("review")}>
                  Review &amp; Submit
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" data-testid="recipe-back-edit" onClick={() => setStep("edit")}>Back to edit</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="recipe-submit-approval" disabled={submitting} onClick={() => setConfirm("submit")}>
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit for Approval"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-amber-600" /> {confirm === "submit" ? "Submit recipe for approval" : "Save recipe draft"}</AlertDialogTitle>
            <AlertDialogDescription>
              {productChanged && (
                <span className="mb-2 block rounded bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">
                  <strong>Re-link:</strong> this recipe will be re-pointed from <strong>{initialProductCode}</strong> to{" "}
                  <strong>{selectedProduct?.productId}</strong> ({selectedProduct?.name}).
                </span>
              )}
              {confirm === "submit"
                ? <>This saves the recipe and submits it for approval in the shared ERP for <strong>{selectedProduct?.name}</strong>. Proceed?</>
                : <>This saves a <strong>Draft</strong> recipe for <strong>{selectedProduct?.name}</strong> in the shared ERP. It will not be usable for production until approved. Proceed?</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="recipe-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="recipe-confirm-save" className="bg-amber-700 hover:bg-amber-800" onClick={doConfirm}>Yes, {confirm === "submit" ? "submit" : "save draft"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// Merge stored ingredients + subComponents into unified editor lines (with kind).
function mergeComponents(iv) {
  const ing = Array.isArray(iv.ingredients) ? iv.ingredients : [];
  const sub = Array.isArray(iv.subComponents) ? iv.subComponents : [];
  const rows = [
    ...ing.map((l) => ({ kind: "ingredient", ingredientId: l.ingredientId || "", refId: "", name: l.name || "", unit: l.unit || "", qty: l.qty ?? l.quantity ?? "" })),
    ...sub.map((l) => ({ kind: "product", ingredientId: "", refId: l.productRef || l.refId || "", name: l.name || "", unit: l.unit || "", qty: l.qty ?? l.quantity ?? "" })),
  ];
  return rows.length ? rows : null;
}

function CostRow({ label, value, strong }) {  return (
    <div className="flex justify-between py-0.5 text-sm">
      <span className={strong ? "font-semibold text-stone-800" : "text-stone-500"}>{label}</span>
      <span className={strong ? "font-mono font-semibold text-stone-900" : "font-mono text-stone-700"}>{value}</span>
    </div>
  );
}
